import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import { Link } from '@/components/links';
import { StackCards } from '@/components/StackCards';
import { Card } from '@/components/StackCards/types';
import IconCard from '@/assets/icons/heroIcons/Card-front.svg';
import IconCards from '@/assets/icons/heroIcons/Cards.svg';
import IconDeferredPayment from '@/assets/icons/heroIcons/Deferred-payment.svg';
import IconDocuments from '@/assets/icons/heroIcons/Documents.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconLoan from '@/assets/icons/heroIcons/Loan.svg';
import IconPay from '@/assets/icons/heroIcons/Pay.svg';
import IconPayment from '@/assets/icons/heroIcons/Payment.svg';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
import Hero from '@/sections/Hero';
import MoreInfoSection from '@/sections/MoreInfo';
import { FaqIntro } from '@/sections/FaqIntro';
import { Button } from '@/components/buttons';
import { InfoBoxes } from '@/components/boxes';
import { BenefitTiles } from '@/components/tiles';
import { IconTile } from '@/components/IconTile';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { SectionHeadline } from '@/components/SectionHeadline';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { Trans, useTranslation } from 'react-i18next';
import { LINKS } from '@/constants/links';
import React from 'react';
import { mergeClasses } from '@/utils/style';
import { NumericRange } from '@/types/range';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { SeoHeader } from '@/components/SeoHeader';
import { Text, Headline } from '@/components/typography';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { getSectionAnchorId } from '@/utils/anchors';
import { useProductRegistrationButtons, useProductRegistrationLink } from '@/hooks/useProductRegistrationLink';
interface Props extends IntlProps {
  menuPromoBanners?: PageMenuPromoBanner[];
  pageBanner: PageBanner | null;
}
export default function Page(props) {
  const $ = _c(197);
  usePageView("money-transfer");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["moneyTransfer", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    pageBanner,
    menuPromoBanners
  } = props;
  const registrationLink = useProductRegistrationLink("prevod-penez");
  const heroButtons = useProductRegistrationButtons(pageBanner?.buttons, "prevod-penez");
  let t1;
  if ($[1] !== registrationLink || $[2] !== t) {
    t1 = Array.from({
      length: 4
    }, (_, index) => {
      const cardIndex = index + 1 as NumericRange<1, 4>;
      return {
        index,
        menuTitle: "",
        title: <div className={mergeClasses(cardIndex === 4 && "text-center lg:text-left")}>{t(`howItWorks.items.${cardIndex}.title`)}</div>,
        ...(cardIndex < 4 ? {
          description: t(`howItWorks.items.${cardIndex}.text`)
        } : {
          footer: <Button analyticsParam="moneyTransferHowItWorksiWantMaxi" testId="moneyTransferHowItWorksiWantMaxi" variant="primary" size="lg" className="m-auto w-full md:w-auto lg:m-0" to={registrationLink}>{t(`howItWorks.items.${cardIndex}.text`)}</Button>
        }),
        images: {
          desktop: {
            src: `/images/sections/moneyTransfer/cards/cs/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.title`)
          },
          mobile: {
            src: `/images/sections/moneyTransfer/cards/cs/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.title`)
          },
          ...(cardIndex < 4 && {
            variant: "appMockup"
          })
        }
      };
    });
    $[1] = registrationLink;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const cards = t1;
  let t2;
  if ($[4] !== t) {
    t2 = t("seo.title");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t) {
    t3 = t("seo.description");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <SeoHeader title={t2} description={t3} />;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = pageBanner?.videoId;
  let t6;
  if ($[11] !== pageBanner?.title || $[12] !== t) {
    t6 = pageBanner?.title ?? t("banner.title");
    $[11] = pageBanner?.title;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== pageBanner?.text || $[15] !== t) {
    t7 = pageBanner?.text ?? t("banner.text");
    $[14] = pageBanner?.text;
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== heroButtons || $[18] !== registrationLink || $[19] !== t) {
    t8 = heroButtons ?? [{
      ariaLabel: t("banner.cta"),
      label: t("banner.cta"),
      to: registrationLink,
      testId: "moneyTransferHeroPrimaryCTA",
      analyticsParam: "moneyTransferHeroPrimaryCTA",
      variant: "primary"
    }];
    $[17] = heroButtons;
    $[18] = registrationLink;
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = pageBanner?.image?.url ?? "/images/sections/moneyTransfer/hero/cs/hero.png";
  let t10;
  if ($[21] !== pageBanner?.image?.alt || $[22] !== t) {
    t10 = pageBanner?.image?.alt ?? t("banner.title");
    $[21] = pageBanner?.image?.alt;
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t9) {
    t11 = {
      url: t9,
      alt: t10
    };
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const t12 = pageBanner?.variant || "mockupMobile";
  let t13;
  if ($[27] !== t11 || $[28] !== t12 || $[29] !== t5 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8) {
    t13 = <Hero videoId={t5} title={t6} text={t7} buttons={t8} image={t11} variant={t12} />;
    $[27] = t11;
    $[28] = t12;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="bg-blue-50"><SectionDelimiter color="bg-blue-100" background="bg-transparent" /></div>;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = getSectionAnchorId("advantages.title");
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t) {
    t16 = t("advantages.title");
    $[36] = t;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t16) {
    t17 = <SectionHeadline className="mb-10 lg:mb-16" anchorKey="advantages.title">{t16}</SectionHeadline>;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t) {
    t18 = t("advantages.items.1.title");
    $[40] = t;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t) {
    t19 = t("advantages.items.1.text");
    $[42] = t;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <IconPayment />;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t18 || $[46] !== t19) {
    t21 = {
      title: t18,
      paragraph: t19,
      icon: t20
    };
    $[45] = t18;
    $[46] = t19;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t) {
    t22 = t("advantages.items.2.title");
    $[48] = t;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t) {
    t23 = t("advantages.items.2.text");
    $[50] = t;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <IconLoan />;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t22 || $[54] !== t23) {
    t25 = {
      title: t22,
      paragraph: t23,
      icon: t24
    };
    $[53] = t22;
    $[54] = t23;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t) {
    t26 = t("advantages.items.3.title");
    $[56] = t;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t) {
    t27 = t("advantages.items.3.text");
    $[58] = t;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <IconPay />;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== t26 || $[62] !== t27) {
    t29 = {
      title: t26,
      paragraph: t27,
      icon: t28
    };
    $[61] = t26;
    $[62] = t27;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== t21 || $[65] !== t25 || $[66] !== t29) {
    t30 = <InfoBoxes items={[t21, t25, t29]} variant="basic" />;
    $[64] = t21;
    $[65] = t25;
    $[66] = t29;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] !== t) {
    t31 = t("advantages.cta");
    $[68] = t;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== registrationLink || $[71] !== t31) {
    t32 = <Button to={registrationLink} testId="moneyTransferRegistration" analyticsParam="moneyTransferRegistration" size="lg" variant="primary" align="center" className="mt-12 md:mt-16">{t31}</Button>;
    $[70] = registrationLink;
    $[71] = t31;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] !== t17 || $[74] !== t30 || $[75] !== t32) {
    t33 = <div className="relative z-10">{t17}{t30}{t32}</div>;
    $[73] = t17;
    $[74] = t30;
    $[75] = t32;
    $[76] = t33;
  } else {
    t33 = $[76];
  }
  let t34;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <div className="bgCircle absolute top-0 left-0 -translate-x-1/4" />;
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  let t35;
  if ($[78] !== t33) {
    t35 = <div className="relative z-30 bg-blue-100 px-4 py-10 lg:py-20" id={t15}>{t33}{t34}</div>;
    $[78] = t33;
    $[79] = t35;
  } else {
    t35 = $[79];
  }
  let t36;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = getSectionAnchorId("howItWorks.title");
    $[80] = t36;
  } else {
    t36 = $[80];
  }
  let t37;
  if ($[81] !== t) {
    t37 = t("howItWorks.title");
    $[81] = t;
    $[82] = t37;
  } else {
    t37 = $[82];
  }
  let t38;
  if ($[83] !== t37) {
    t38 = <SectionHeadline className="mb-10 px-4 md:mb-16" anchorKey="howItWorks.title">{t37}</SectionHeadline>;
    $[83] = t37;
    $[84] = t38;
  } else {
    t38 = $[84];
  }
  let t39;
  if ($[85] !== cards) {
    t39 = <StackCards imagePosition="left" cards={cards} />;
    $[85] = cards;
    $[86] = t39;
  } else {
    t39 = $[86];
  }
  let t40;
  if ($[87] !== t38 || $[88] !== t39) {
    t40 = <div className="relative z-10">{t38}{t39}</div>;
    $[87] = t38;
    $[88] = t39;
    $[89] = t40;
  } else {
    t40 = $[89];
  }
  let t41;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <div className="absolute top-3/4 right-0 overflow-hidden"><div className="bgCircle relative top-0 right-0 translate-x-1/3 rotate-90" /></div>;
    $[90] = t41;
  } else {
    t41 = $[90];
  }
  let t42;
  if ($[91] !== t40) {
    t42 = <div className="relative bg-blue-100 pt-10 pb-20 lg:pt-20 lg:pb-40" id={t36}>{t40}{t41}</div>;
    $[91] = t40;
    $[92] = t42;
  } else {
    t42 = $[92];
  }
  let t43;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <SectionDelimiter color="bg-blue-50" background="bg-blue-100" />;
    $[93] = t43;
  } else {
    t43 = $[93];
  }
  let t44;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = getSectionAnchorId("itsWorthIt.title");
    $[94] = t44;
  } else {
    t44 = $[94];
  }
  let t45;
  if ($[95] !== t) {
    t45 = t("itsWorthIt.title");
    $[95] = t;
    $[96] = t45;
  } else {
    t45 = $[96];
  }
  let t46;
  if ($[97] !== t45) {
    t46 = <SectionHeadline className="mb-4 px-0" anchorKey="itsWorthIt.title">{t45}</SectionHeadline>;
    $[97] = t45;
    $[98] = t46;
  } else {
    t46 = $[98];
  }
  let t47;
  if ($[99] !== t) {
    t47 = t("itsWorthIt.subtitle");
    $[99] = t;
    $[100] = t47;
  } else {
    t47 = $[100];
  }
  let t48;
  if ($[101] !== t47) {
    t48 = <Headline as="h3" variant="h5" anchorKey="itsWorthIt.subtitle" className="mb-10 lg:mb-16">{t47}</Headline>;
    $[101] = t47;
    $[102] = t48;
  } else {
    t48 = $[102];
  }
  let t49;
  if ($[103] !== t46 || $[104] !== t48) {
    t49 = <div className="mx-auto w-full max-w-[50rem] text-center">{t46}{t48}</div>;
    $[103] = t46;
    $[104] = t48;
    $[105] = t49;
  } else {
    t49 = $[105];
  }
  let t50;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <Link to={LINKS().card} testId="payInThreeWhereToShop" analyticsParam="payInThreeWhereToShop" />;
    $[106] = t50;
  } else {
    t50 = $[106];
  }
  let t51;
  if ($[107] !== t) {
    t51 = <Trans t={t} i18nKey="itsWorthIt.items.1">{t50}</Trans>;
    $[107] = t;
    $[108] = t51;
  } else {
    t51 = $[108];
  }
  let t52;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <IconTile icon={<IconCard />} />;
    $[109] = t52;
  } else {
    t52 = $[109];
  }
  let t53;
  if ($[110] !== t51) {
    t53 = {
      title: t51,
      icon: t52
    };
    $[110] = t51;
    $[111] = t53;
  } else {
    t53 = $[111];
  }
  let t54;
  if ($[112] !== t) {
    t54 = t("itsWorthIt.items.3");
    $[112] = t;
    $[113] = t54;
  } else {
    t54 = $[113];
  }
  let t55;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <IconTile icon={<IconDocuments />} />;
    $[114] = t55;
  } else {
    t55 = $[114];
  }
  let t56;
  if ($[115] !== t54) {
    t56 = {
      title: t54,
      icon: t55
    };
    $[115] = t54;
    $[116] = t56;
  } else {
    t56 = $[116];
  }
  let t57;
  if ($[117] !== t) {
    t57 = t("itsWorthIt.items.4");
    $[117] = t;
    $[118] = t57;
  } else {
    t57 = $[118];
  }
  let t58;
  if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <IconTile icon={<IconPayment />} />;
    $[119] = t58;
  } else {
    t58 = $[119];
  }
  let t59;
  if ($[120] !== t57) {
    t59 = {
      title: t57,
      icon: t58
    };
    $[120] = t57;
    $[121] = t59;
  } else {
    t59 = $[121];
  }
  let t60;
  if ($[122] !== t) {
    t60 = t("itsWorthIt.items.6");
    $[122] = t;
    $[123] = t60;
  } else {
    t60 = $[123];
  }
  let t61;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <IconTile icon={<IconCards />} />;
    $[124] = t61;
  } else {
    t61 = $[124];
  }
  let t62;
  if ($[125] !== t60) {
    t62 = {
      title: t60,
      icon: t61
    };
    $[125] = t60;
    $[126] = t62;
  } else {
    t62 = $[126];
  }
  let t63;
  if ($[127] !== t53 || $[128] !== t56 || $[129] !== t59 || $[130] !== t62) {
    t63 = <BenefitTiles items={[t53, t56, t59, t62]} />;
    $[127] = t53;
    $[128] = t56;
    $[129] = t59;
    $[130] = t62;
    $[131] = t63;
  } else {
    t63 = $[131];
  }
  let t64;
  if ($[132] !== t) {
    t64 = t("itsWorthIt.text");
    $[132] = t;
    $[133] = t64;
  } else {
    t64 = $[133];
  }
  let t65;
  if ($[134] !== t64) {
    t65 = <Text variant="p4" className="my-10 text-center text-gray-600 lg:my-12">{t64}</Text>;
    $[134] = t64;
    $[135] = t65;
  } else {
    t65 = $[135];
  }
  let t66;
  if ($[136] !== t) {
    t66 = t("itsWorthIt.cta");
    $[136] = t;
    $[137] = t66;
  } else {
    t66 = $[137];
  }
  let t67;
  if ($[138] !== registrationLink || $[139] !== t66) {
    t67 = <Button to={registrationLink} testId="moneyTransferRevolving" analyticsParam="moneyTransferRevolving" size="lg" variant="primary" align="center">{t66}</Button>;
    $[138] = registrationLink;
    $[139] = t66;
    $[140] = t67;
  } else {
    t67 = $[140];
  }
  let t68;
  if ($[141] !== t49 || $[142] !== t63 || $[143] !== t65 || $[144] !== t67) {
    t68 = <div className="relative z-10">{t49}{t63}{t65}{t67}</div>;
    $[141] = t49;
    $[142] = t63;
    $[143] = t65;
    $[144] = t67;
    $[145] = t68;
  } else {
    t68 = $[145];
  }
  let t69;
  if ($[146] === Symbol.for("react.memo_cache_sentinel")) {
    t69 = <div className="bgCircle absolute top-0 left-0 -translate-x-1/2 translate-y-1/4" />;
    $[146] = t69;
  } else {
    t69 = $[146];
  }
  let t70;
  if ($[147] !== t68) {
    t70 = <div className="relative bg-blue-50 px-4 pt-10 pb-20 lg:pt-20 lg:pb-40" id={t44}>{t68}{t69}</div>;
    $[147] = t68;
    $[148] = t70;
  } else {
    t70 = $[148];
  }
  let t71;
  if ($[149] === Symbol.for("react.memo_cache_sentinel")) {
    t71 = <SectionDelimiter color="bg-blue-100" background="bg-blue-50" />;
    $[149] = t71;
  } else {
    t71 = $[149];
  }
  let t72;
  if ($[150] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = LINKS();
    $[150] = t72;
  } else {
    t72 = $[150];
  }
  let t73;
  if ($[151] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = <Link to="/caste-dotazy/dalsi-caste-otazky" analyticsParam="faqMoneyTransfer" testId="faqMoneyTransfer" />;
    $[151] = t73;
  } else {
    t73 = $[151];
  }
  let t74;
  if ($[152] !== t) {
    t74 = <div className="py-10 lg:py-20"><FaqIntro link={t72.faq} text={<Trans i18nKey="faq.text" t={t}>{t73}</Trans>} /></div>;
    $[152] = t;
    $[153] = t74;
  } else {
    t74 = $[153];
  }
  let t75;
  if ($[154] === Symbol.for("react.memo_cache_sentinel")) {
    t75 = <IconDeferredPayment className="fillButtonIcon" />;
    $[154] = t75;
  } else {
    t75 = $[154];
  }
  let t76;
  if ($[155] !== t) {
    t76 = t("core:link.deferredPayment");
    $[155] = t;
    $[156] = t76;
  } else {
    t76 = $[156];
  }
  let t77;
  if ($[157] === Symbol.for("react.memo_cache_sentinel")) {
    t77 = LINKS();
    $[157] = t77;
  } else {
    t77 = $[157];
  }
  let t78;
  if ($[158] !== t76) {
    t78 = {
      analyticsParam: "moneyTransferMoreInfoDeferredPayment",
      icon: t75,
      testId: "moneyTransferMoreInfoDeferredPayment",
      title: t76,
      to: t77.deferredPayment
    };
    $[158] = t76;
    $[159] = t78;
  } else {
    t78 = $[159];
  }
  let t79;
  if ($[160] === Symbol.for("react.memo_cache_sentinel")) {
    t79 = <IconMaxi className="fillButtonIcon" />;
    $[160] = t79;
  } else {
    t79 = $[160];
  }
  let t80;
  if ($[161] !== t) {
    t80 = t("core:products.revolving");
    $[161] = t;
    $[162] = t80;
  } else {
    t80 = $[162];
  }
  let t81;
  if ($[163] === Symbol.for("react.memo_cache_sentinel")) {
    t81 = LINKS();
    $[163] = t81;
  } else {
    t81 = $[163];
  }
  let t82;
  if ($[164] !== t80) {
    t82 = {
      analyticsParam: "moneyTransferMoreInfoRevolvingApplication",
      icon: t79,
      testId: "moneyTransferMoreInfoRevolvingApplication",
      title: t80,
      to: t81.revolving
    };
    $[164] = t80;
    $[165] = t82;
  } else {
    t82 = $[165];
  }
  let t83;
  if ($[166] === Symbol.for("react.memo_cache_sentinel")) {
    t83 = <IconThird className="fillButtonIcon" />;
    $[166] = t83;
  } else {
    t83 = $[166];
  }
  let t84;
  if ($[167] !== t) {
    t84 = t("core:products.payInThree");
    $[167] = t;
    $[168] = t84;
  } else {
    t84 = $[168];
  }
  let t85;
  if ($[169] === Symbol.for("react.memo_cache_sentinel")) {
    t85 = LINKS();
    $[169] = t85;
  } else {
    t85 = $[169];
  }
  let t86;
  if ($[170] !== t84) {
    t86 = {
      analyticsParam: "moneyTransferMoreInfoPayInThree",
      icon: t83,
      testId: "moneyTransferMoreInfoPayInThree",
      title: t84,
      to: t85.payInThree
    };
    $[170] = t84;
    $[171] = t86;
  } else {
    t86 = $[171];
  }
  let t87;
  if ($[172] === Symbol.for("react.memo_cache_sentinel")) {
    t87 = <IconCard className="fillButtonIcon" />;
    $[172] = t87;
  } else {
    t87 = $[172];
  }
  let t88;
  if ($[173] !== t) {
    t88 = t("core:products.card");
    $[173] = t;
    $[174] = t88;
  } else {
    t88 = $[174];
  }
  let t89;
  if ($[175] === Symbol.for("react.memo_cache_sentinel")) {
    t89 = LINKS();
    $[175] = t89;
  } else {
    t89 = $[175];
  }
  let t90;
  if ($[176] !== t88) {
    t90 = {
      analyticsParam: "moneyTransferMoreInfoCard",
      icon: t87,
      testId: "moneyTransferMoreInfoCard",
      title: t88,
      to: t89.card
    };
    $[176] = t88;
    $[177] = t90;
  } else {
    t90 = $[177];
  }
  let t91;
  if ($[178] !== t78 || $[179] !== t82 || $[180] !== t86 || $[181] !== t90) {
    t91 = <MoreInfoSection buttons={[t78, t82, t86, t90]} />;
    $[178] = t78;
    $[179] = t82;
    $[180] = t86;
    $[181] = t90;
    $[182] = t91;
  } else {
    t91 = $[182];
  }
  let t92;
  if ($[183] !== t74 || $[184] !== t91) {
    t92 = <div className="relative z-10">{t74}{t91}</div>;
    $[183] = t74;
    $[184] = t91;
    $[185] = t92;
  } else {
    t92 = $[185];
  }
  let t93;
  if ($[186] === Symbol.for("react.memo_cache_sentinel")) {
    t93 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[186] = t93;
  } else {
    t93 = $[186];
  }
  let t94;
  if ($[187] !== t92) {
    t94 = <div className="relative overflow-hidden bg-blue-100">{t92}{t93}</div>;
    $[187] = t92;
    $[188] = t94;
  } else {
    t94 = $[188];
  }
  let t95;
  if ($[189] !== menuPromoBanners || $[190] !== t13 || $[191] !== t35 || $[192] !== t4 || $[193] !== t42 || $[194] !== t70 || $[195] !== t94) {
    t95 = <Layout menuPromoBanners={menuPromoBanners}>{t4}{t13}{t14}{t35}{t42}{t43}{t70}{t71}{t94}</Layout>;
    $[189] = menuPromoBanners;
    $[190] = t13;
    $[191] = t35;
    $[192] = t4;
    $[193] = t42;
    $[194] = t70;
    $[195] = t94;
    $[196] = t95;
  } else {
    t95 = $[196];
  }
  return t95;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('prevod-penez');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      pageBanner: await getPageBannerBySlug('money-transfer'),
      ...(await getIntlProps(ctx, ['core', 'moneyTransfer']))
    }
  };
}) satisfies GetStaticProps<Props>;